import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

export const LeadCapture = ({ innerRef }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/signups`, { first_name: firstName.trim(), email: email.trim() });
      setStatus("done");
      toast.success("Beautiful. You're on the list.");
    } catch (err) {
      setStatus("idle");
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please enter a valid email.");
    }
  };

  return (
    <section ref={innerRef} data-testid="lead-capture-section" className="blush-fade py-24 md:py-36">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: EASE }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center"
        >
          {/* Left statement */}
          <div>
            <p className="font-script text-peach-deep text-4xl md:text-5xl leading-none">yes, please</p>
            <h2 className="mt-3 flex items-center gap-3 font-display font-black text-5xl md:text-7xl leading-[0.94] tracking-tight text-ink">
              Send it to me
              <ArrowRight className="h-10 w-10 md:h-14 md:w-14 flex-none" strokeWidth={2.25} />
            </h2>
            <p className="mt-8 max-w-md text-xl font-light text-ink-muted leading-relaxed">
              Enter your name and email, and your free audio lands straight in your inbox.
            </p>
          </div>

          {/* Right form */}
          <div className="bg-cream px-8 py-12 md:px-14 md:py-16 shadow-[0_40px_100px_-50px_rgba(23,18,15,0.4)]">
            {status === "done" ? (
              <div data-testid="signup-success" className="flex flex-col items-center text-center py-6">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-rose text-cream">
                  <Check size={28} />
                </span>
                <p className="mt-6 font-display italic text-3xl text-ink">You're all set, {firstName.trim()}.</p>
                <p className="mt-2 text-ink-muted font-light">Your meditation is coming to your inbox soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="signup-form" className="space-y-8">
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-ink-muted mb-3">First name</label>
                  <input
                    data-testid="first-name-input"
                    type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-transparent border-b-2 border-peach pb-3 text-xl font-light text-ink placeholder:text-ink-muted/40 outline-none transition-colors duration-300 focus:border-peach-deep"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-ink-muted mb-3">Email</label>
                  <input
                    data-testid="email-input"
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-transparent border-b-2 border-peach pb-3 text-xl font-light text-ink placeholder:text-ink-muted/40 outline-none transition-colors duration-300 focus:border-peach-deep"
                  />
                </div>

                <button
                  data-testid="signup-submit-button"
                  type="submit" disabled={status === "loading"}
                  className="group flex w-full items-center justify-center gap-3 bg-rose text-cream text-sm font-semibold uppercase tracking-[0.2em] px-8 py-5 transition-colors duration-300 hover:bg-rose-hover active:scale-[0.99] disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>Sending<Loader2 size={16} className="animate-spin" /></>
                  ) : (
                    <>Send it to me<ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LeadCapture;
